import { useState } from "react";
import { ImagePlus, X } from "lucide-react";
import { toast } from "sonner";
import { errMsg, fileUrl } from "@/lib/api";
import { applyBranding, hexToHsl, SURFACES } from "@/lib/branding";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Spinner } from "@/components/States";
import { uploadFile } from "@/components/project/PhotosTab";

export function ColorField({ id, label, value, onChange }) {
  const valid = !!hexToHsl(value);
  return (
    <div className="space-y-2">
      <Label htmlFor={id}>{label}</Label>
      <div className="flex items-center gap-2">
        <input type="color" aria-label={`${label} picker`} value={valid ? value : "#000000"} onChange={(e) => onChange(e.target.value)} className="h-10 w-12 cursor-pointer rounded-md border bg-transparent p-1" data-testid={`${id}-picker`} />
        <Input id={id} value={value} onChange={(e) => onChange(e.target.value)} className={`font-mono ${valid ? "" : "border-destructive"}`} data-testid={`${id}-input`} />
      </div>
    </div>
  );
}

export function ImageField({ id, label, hint, value, onChange }) {
  const [busy, setBusy] = useState(false);
  const pick = async (file) => {
    setBusy(true);
    try { const f = await uploadFile(file, "", "brand"); onChange(f.url); } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };
  return (
    <div className="space-y-2">
      <Label className="block">{label}</Label>
      {value && (
        <div className="relative w-fit">
          <img src={fileUrl(value)} alt="" className={`rounded-md border object-contain ${id === "logo" ? "h-16 bg-white px-3" : "aspect-video h-28"}`} data-testid={`${id}-preview`} />
          <Button type="button" size="icon" variant="secondary" className="absolute -right-2 -top-2 h-6 w-6 rounded-full" onClick={() => onChange(null)} aria-label={`Remove ${label}`} data-testid={`${id}-remove`}><X className="h-3 w-3" /></Button>
        </div>
      )}
      <label className="inline-flex cursor-pointer items-center gap-2 text-sm underline-offset-4 hover:underline" data-testid={`${id}-upload-label`}>
        {busy ? <Spinner /> : <ImagePlus className="h-4 w-4" />} {value ? "Replace" : "Upload"} {label.toLowerCase()}
        <input type="file" accept="image/*" hidden onChange={(e) => e.target.files[0] && pick(e.target.files[0])} data-testid={`${id}-file-input`} />
      </label>
      {hint && <p className="text-xs text-muted-foreground">{hint}</p>}
    </div>
  );
}

export function BrandPreview({ form }) {
  const dark = hexToHsl(form.primary_color)?.[2] < 60;
  return (
    <div className="overflow-hidden rounded-xl border" data-testid="brand-preview">
      <div className="p-5" style={{ background: form.primary_color, color: dark ? "#fff" : "#1b1b1b" }}>
        {form.logo_url ? <img src={fileUrl(form.logo_url)} alt="" className="h-9 object-contain" /> : <p className="section-title leading-none">{form.name || "Your company"}</p>}
        {form.tagline && <p className="mt-1.5 text-[10px] uppercase tracking-[0.16em] opacity-70">{form.tagline}</p>}
      </div>
      <div className="space-y-3 bg-card p-5">
        <p className="text-xs uppercase tracking-[0.2em] font-semibold" style={{ color: form.accent_color }}>Great Room</p>
        <p className="section-title">Lakeside cottage refresh</p>
        <span className="inline-block rounded-full px-4 py-1.5 text-xs text-white" style={{ background: form.primary_color }}>Primary button</span>
      </div>
    </div>
  );
}

export const previewBranding = (form) => applyBranding(form);

export function SurfaceField({ value, onChange }) {
  return (
    <div className="space-y-2 sm:col-span-2" data-testid="surface-field">
      <Label>Background style</Label>
      <div className="flex flex-wrap gap-2">
        {Object.entries(SURFACES).map(([k, s]) => (
          <button type="button" key={k} onClick={() => onChange(k)} aria-pressed={value === k} data-testid={`surface-${k}`}
            className={`flex items-center gap-2 rounded-full border px-3 py-1.5 text-xs transition-colors ${value === k ? "border-primary ring-2 ring-primary/30" : "hover:bg-secondary"}`}>
            <span className="h-4 w-4 rounded-full border" style={{ background: s.swatch }} />{s.label}
          </button>
        ))}
      </div>
      <p className="text-xs text-muted-foreground">Sets the page background for your whole team and your clients. Your primary and accent colours stay the same.</p>
    </div>
  );
}
